import type { CSSProperties } from "react";
import { useEmbedTokens } from "../theme";
import { statusPresentation, type StatusDomain, type StatusTone } from "../status";

// Account-area redesign Task 6 (Task-5 gate advisory 4): `value` is optional
// — wire rows are runtime data, and a malformed row with a MISSING status
// must degrade to humanizeEnum's empty marker (the em dash, neutral tone),
// not crash the whole panel on `.replace`.
type Props = { domain: StatusDomain; value?: string } | { tone: StatusTone; label: string };

/**
 * A status pill. Pass `domain` + `value` to read the label and tone from the
 * one status map (embed-shared/status.ts); pass `tone` + `label` for a
 * one-off. A missing/empty `value` renders the empty marker in neutral. The
 * text is never transformed with CSS, so the DOM text is exactly
 * what the customer reads, and colour is never the only signal because the
 * label always names the state.
 */
export function EmbedStatusBadge(props: Props) {
  const t = useEmbedTokens();
  const { label, tone } =
    "domain" in props ? statusPresentation(props.domain, props.value ?? "") : props;
  const c = t.colors;
  const byTone: Record<StatusTone, { bg: string; fg: string }> = {
    success: { bg: c.successSoft, fg: c.successGreen },
    warning: { bg: c.warningSoft, fg: c.warning },
    danger: { bg: c.dangerSoft, fg: c.error },
    info: { bg: c.infoSoft, fg: c.info },
    neutral: { bg: c.neutralSoft, fg: c.neutral },
  };
  const style: CSSProperties = {
    display: "inline-block",
    background: byTone[tone].bg, color: byTone[tone].fg,
    borderRadius: 999, padding: "3px 8px",
    fontSize: t.fontSize.xs, fontWeight: t.fontWeight.medium,
    lineHeight: t.lineHeight.tight, whiteSpace: "nowrap",
  };
  return <span data-status-tone={tone} style={style}>{label}</span>;
}
